import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Home, Target, Briefcase, CheckCircle, Wallet, HeartPulse, CreditCard,
  Calculator, FileText, Shield, ArrowLeft, ArrowRight, Check, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track, getVisitorId } from "@/lib/tracker";
import { CONSENT_TEXT } from "@/config";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useSeo } from "@/lib/seo";

const ICONS = {
  users: Users, home: Home, target: Target, briefcase: Briefcase, "check-circle": CheckCircle,
  wallet: Wallet, "heart-pulse": HeartPulse, "credit-card": CreditCard, calculator: Calculator,
  "file-text": FileText, shield: Shield, sparkles: Sparkles,
};

function quiFromEntourage(flow, ids) {
  const quis = flow.entourage.options.filter((o) => ids.includes(o.id) && o.qui).map((o) => o.qui);
  if (!quis.length) return "vos proches";
  if (quis.length === 1) return quis[0];
  return quis.slice(0, -1).join(", ") + " et " + quis[quis.length - 1];
}

export default function Diagnostic() {
  const [flow, setFlow] = useState(null);
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({ profile: null, projet: null, entourage: [], prepare: [], risques: {} });
  const [lead, setLead] = useState({ prenom: "", telephone: "", courriel: "", consent: false });
  const [leadDone, setLeadDone] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  useSeo("Diagnostic 5 minutes — Mon Projet Protégé", "Évaluez votre protection financière en 5 minutes et obtenez votre carte de protection personnalisée.");

  useEffect(() => { api.questions().then(setFlow).catch(() => {}); track("diagnostic_start"); }, []);

  const screens = useMemo(() => {
    if (!flow) return [];
    return [
      { type: "single", key: "profile", ...flow.profile },
      { type: "single", key: "projet", ...flow.projet },
      { type: "multi", key: "entourage", ...flow.entourage },
      { type: "capture", icon: "sparkles", question: "Personnalisons votre carte de protection", subtitle: "Dites-nous à qui elle s'adresse : on adapte vos résultats et on vous envoie votre carte personnalisée." },
      { type: "multi", key: "prepare", ...flow.prepare },
      ...flow.risques.map((r) => ({ type: "risk", ...r })),
    ];
  }, [flow]);

  const qui = useMemo(() => (flow ? quiFromEntourage(flow, ans.entourage) : "vos proches"), [flow, ans.entourage]);

  if (!flow) return <div data-testid="diagnostic-loading" className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Chargement du diagnostic…</div>;

  const total = screens.length;
  const sc = screens[step];
  const Icon = ICONS[sc.icon] || Shield;

  const isValid = () => {
    if (sc.type === "single") return !!ans[sc.key];
    if (sc.type === "multi") return ans[sc.key].length > 0;
    if (sc.type === "capture") return leadDone;
    if (sc.type === "risk") return ans.risques[sc.id] !== undefined;
    return false;
  };

  const progress = Math.round(((step + (isValid() ? 1 : 0)) / total) * 100);

  const chooseSingle = (opt) => setAns((a) => ({ ...a, [sc.key]: opt.id }));
  const toggleMulti = (opt) => setAns((a) => {
    let arr = a[sc.key];
    const noneId = sc.options.find((o) => o.none)?.id;
    if (opt.none) arr = arr.includes(opt.id) ? [] : [opt.id];
    else arr = arr.includes(opt.id) ? arr.filter((x) => x !== opt.id) : [...arr.filter((x) => x !== noneId), opt.id];
    return { ...a, [sc.key]: arr };
  });
  const chooseRisk = (opt) => setAns((a) => ({ ...a, risques: { ...a.risques, [sc.id]: opt.risk } }));

  const submitLead = async () => {
    if (!lead.prenom.trim()) return toast.error("Votre prénom est requis.");
    if (!lead.telephone.trim()) return toast.error("Votre téléphone est requis.");
    if (!lead.courriel.trim()) return toast.error("Votre courriel est requis.");
    if (lead.telephone.replace(/\D/g, "").length < 10) return toast.error("Numéro de téléphone invalide (au moins 10 chiffres).");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.courriel.trim())) return toast.error("Adresse courriel invalide.");
    if (!lead.consent) return toast.error("Le consentement est requis (Loi 25).");
    setSending(true);
    try {
      const projetLabel = flow.projet.options.find((o) => o.id === ans.projet)?.label || null;
      await api.createLead({ prenom: lead.prenom, telephone: lead.telephone, courriel: lead.courriel, consentement: true, consentement_texte: CONSENT_TEXT, profil: projetLabel, source: "Diagnostic", visitor_id: getVisitorId() });
      localStorage.setItem("mpp_contact_done", "1");
      setLeadDone(true);
      toast.success("Merci ! On personnalise votre carte.");
      setStep((s) => s + 1);
    } catch {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  const next = () => {
    if (sc.type === "capture") return submitLead();
    if (step < total - 1) setStep((s) => s + 1);
    else navigate("/resultat", { state: { payload: { profile: ans.profile, projet: ans.projet, entourage: ans.entourage, prepare: ans.prepare, risques: ans.risques } } });
  };
  const back = () => step > 0 && setStep((s) => s - 1);

  const questionText = sc.type === "risk" ? sc.question.replace("{qui}", qui) : sc.question;

  return (
    <div data-testid="diagnostic-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-slate-500">Étape {step + 1} sur {total}</span>
        <span className="text-sm font-bold text-[#C89A3E]">{progress}%</span>
      </div>
      <div data-testid="diagnostic-progress-bar" className="h-2.5 w-full rounded-full bg-[#012141]/10 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: "#C89A3E" }} />
      </div>
      <div className="mt-4 flex items-center gap-1.5 flex-wrap">
        {screens.map((_, i) => (<span key={i} className="h-2 rounded-full transition-all duration-300" style={{ width: i === step ? 28 : 10, backgroundColor: i <= step ? "#012141" : "#E2E8F0" }} />))}
      </div>

      <div key={step} className="slide-enter mt-10">
        <span className="grid place-items-center w-16 h-16 rounded-3xl bg-[#012141] text-[#C89A3E] shadow-lg"><Icon size={30} /></span>
        <h1 data-testid="diagnostic-question" className="mt-6 text-2xl sm:text-3xl font-bold text-[#012141] font-heading leading-tight">{questionText}</h1>
        {sc.subtitle && <p className="mt-2 text-base text-slate-500">{sc.subtitle}</p>}

        {/* ---- Single / Risk choices ---- */}
        {(sc.type === "single" || sc.type === "risk") && (
          <div className="mt-8 space-y-3">
            {sc.options.map((opt, i) => {
              const active = sc.type === "risk" ? ans.risques[sc.id] === opt.risk : ans[sc.key] === opt.id;
              return (
                <button key={opt.id} data-testid={`diagnostic-option-card-${i}`} onClick={() => (sc.type === "risk" ? chooseRisk(opt) : chooseSingle(opt))}
                  className={`no-tap-highlight w-full text-left flex items-center gap-4 px-5 py-5 rounded-2xl border-2 transition-all duration-200 ${active ? "border-[#012141] bg-[#012141]/5 shadow-md" : "border-[#012141]/12 bg-white hover:border-[#C89A3E] hover:bg-[#FDFDFD]"}`}>
                  <span className={`grid place-items-center w-7 h-7 rounded-full border-2 flex-shrink-0 transition-all ${active ? "border-[#012141] bg-[#012141] text-[#C89A3E]" : "border-slate-300 text-transparent"}`}><Check size={16} /></span>
                  <span className={`text-base font-semibold ${active ? "text-[#012141]" : "text-slate-700"}`}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ---- Multi choices ---- */}
        {sc.type === "multi" && (
          <div className="mt-8 space-y-3">
            {sc.options.map((opt, i) => {
              const active = ans[sc.key].includes(opt.id);
              return (
                <button key={opt.id} data-testid={`diagnostic-option-card-${i}`} onClick={() => toggleMulti(opt)}
                  className={`no-tap-highlight w-full text-left flex items-center gap-4 px-5 py-5 rounded-2xl border-2 transition-all duration-200 ${active ? "border-[#012141] bg-[#012141]/5 shadow-md" : "border-[#012141]/12 bg-white hover:border-[#C89A3E] hover:bg-[#FDFDFD]"}`}>
                  <span className={`grid place-items-center w-7 h-7 rounded-lg border-2 flex-shrink-0 transition-all ${active ? "border-[#012141] bg-[#012141] text-[#C89A3E]" : "border-slate-300 text-transparent"}`}><Check size={16} /></span>
                  <span className={`text-base font-semibold ${active ? "text-[#012141]" : "text-slate-700"}`}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ---- Capture ---- */}
        {sc.type === "capture" && (
          <div data-testid="diagnostic-capture" className="mt-8 rounded-2xl border border-[#012141]/15 bg-white p-6 shadow-sm">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-semibold text-[#012141]">Comment doit-on vous appeler ?</label>
                <Input data-testid="lead-prenom" placeholder="Votre prénom" value={lead.prenom} onChange={(e) => setLead({ ...lead, prenom: e.target.value })} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#012141]">Votre numéro de téléphone</label>
                <Input data-testid="lead-telephone" type="tel" placeholder="Votre téléphone" value={lead.telephone} onChange={(e) => setLead({ ...lead, telephone: e.target.value })} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#012141]">Votre courriel</label>
                <Input data-testid="lead-courriel" type="email" placeholder="Votre courriel" value={lead.courriel} onChange={(e) => setLead({ ...lead, courriel: e.target.value })} className="mt-1.5 rounded-xl" />
              </div>
            </div>
            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="lead-consent" checked={lead.consent} onCheckedChange={(v) => setLead({ ...lead, consent: !!v })} className="mt-0.5" />
              <span className="text-xs text-slate-500 leading-relaxed">{CONSENT_TEXT}</span>
            </label>
          </div>
        )}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button data-testid="diagnostic-back-btn" onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-100 transition-all"><ArrowLeft size={18} /> Précédent</button>
        <button data-testid="diagnostic-next-step-btn" onClick={next} disabled={sc.type === "capture" ? sending : !isValid()}
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#012141] text-white font-semibold shadow-lg hover:bg-[#00172E] disabled:opacity-40 transition-all">
          {sc.type === "capture"
            ? (sending ? <><Loader2 className="animate-spin" size={18} /> Envoi…</> : <><Sparkles size={16} /> Personnaliser mes résultats</>)
            : (step === total - 1 ? <>Voir ma carte <ArrowRight size={18} /></> : <>Suivant <ArrowRight size={18} /></>)}
        </button>
      </div>
    </div>
  );
}
